/**
 * Tradução de falhas de câmera — núcleo puro, sem React e sem DOM.
 *
 * Antes a falha de câmera morria num `console.log` dentro do leitor: o
 * participante ficava olhando um retângulo cinza sem saber que precisava
 * liberar a permissão. Aqui a falha vira um objeto com tipo e mensagem, e o
 * componente decide como mostrar.
 *
 * `qr-scanner` rejeita ora com `DOMException` (vinda do getUserMedia), ora com
 * string crua ('Camera not found.'), então as duas formas são cobertas.
 */

export type QrCameraErrorKind =
  | 'permission_denied'
  | 'not_found'
  | 'in_use'
  | 'insecure_context'
  | 'unsupported'
  | 'unknown'

export type QrCameraError = {
  kind: QrCameraErrorKind
  message: string
  cause?: unknown
}

const MESSAGES: Record<QrCameraErrorKind, string> = {
  permission_denied:
    'Precisamos da sua permissão para usar a câmera. Libere o acesso nas configurações do navegador e tente de novo.',
  not_found: 'Não encontramos uma câmera disponível neste dispositivo.',
  in_use:
    'A câmera está sendo usada por outro aplicativo. Feche os outros apps e tente de novo.',
  insecure_context:
    'A câmera só funciona em conexões seguras (https). Abra o link pelo endereço oficial do evento.',
  unsupported:
    'Este navegador não tem suporte à leitura de QR Code pela câmera. Use o campo de código abaixo.',
  unknown: 'Não foi possível iniciar a câmera. Tente de novo.'
}

export const cameraErrorMessage = (kind: QrCameraErrorKind): string =>
  MESSAGES[kind]

const readErrorName = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'name' in error) {
    return String((error as { name?: unknown }).name ?? '')
  }
  return ''
}

const readErrorText = (error: unknown): string => {
  if (typeof error === 'string') return error
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message?: unknown }).message ?? '')
  }
  return ''
}

const kindFromName = (name: string): QrCameraErrorKind | null => {
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return 'permission_denied'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'not_found'
    case 'NotReadableError':
    case 'TrackStartError':
      return 'in_use'
    case 'NotSupportedError':
      return 'unsupported'
    default:
      return null
  }
}

const kindFromText = (text: string): QrCameraErrorKind => {
  const normalized = text.toLowerCase()

  if (
    normalized.includes('permission') ||
    normalized.includes('notallowed') ||
    normalized.includes('denied')
  ) {
    return 'permission_denied'
  }

  if (
    normalized.includes('camera not found') ||
    normalized.includes('no cameras found') ||
    normalized.includes('notfound') ||
    normalized.includes('overconstrained')
  ) {
    return 'not_found'
  }

  if (normalized.includes('notreadable') || normalized.includes('in use')) {
    return 'in_use'
  }

  if (
    normalized.includes('secure context') ||
    normalized.includes('https') ||
    normalized.includes('insecure')
  ) {
    return 'insecure_context'
  }

  if (
    normalized.includes('not supported') ||
    normalized.includes('unsupported') ||
    normalized.includes('mediadevices')
  ) {
    return 'unsupported'
  }

  return 'unknown'
}

export const describeCameraError = (error: unknown): QrCameraError => {
  const kind = kindFromName(readErrorName(error)) ?? kindFromText(readErrorText(error))

  return { kind, message: MESSAGES[kind], cause: error }
}

/**
 * Diagnóstico do ambiente antes de tentar abrir a câmera. Evita cair no erro
 * genérico quando a causa é o contexto (http puro, webview sem suporte).
 */
export const describeCameraSupport = (
  navigatorLike: { mediaDevices?: unknown } | undefined,
  isSecureContext: boolean | undefined
): QrCameraError | null => {
  if (isSecureContext === false) {
    return { kind: 'insecure_context', message: MESSAGES.insecure_context }
  }

  if (!navigatorLike || !navigatorLike.mediaDevices) {
    return { kind: 'unsupported', message: MESSAGES.unsupported }
  }

  return null
}
